import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import type { ComposerStyles } from "./composerStyles";
import type { CloneDayResult } from "./useComposer";

// ⧉ Clone-day control under the day panel (Antenore 2026-06-11: build
// D1 once, clone it, swap exercises, done). Collapsed to one row; open →
// target-day chips. The duplicate guard refuses cleanly and names the
// colliding exercise instead of half-cloning.
export function CloneDayBar({
  dayCount,
  selectedDay,
  hasContent,
  resolveName,
  onClone,
  styles,
}: {
  dayCount: number;
  selectedDay: number;
  hasContent: boolean;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  onClone: (toDay: number) => CloneDayResult;
  styles: ComposerStyles;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasContent || dayCount < 2) return null;

  const clone = (toDay: number) => {
    const r = onClone(toDay);
    if (r.ok) {
      setOpen(false);
      setError(null);
      return;
    }
    if (r.reason === "conflict") {
      setError(
        t("composer.dupExercise", {
          name: resolveName(r.conflict.slug),
          day: r.conflict.dayIdx + 1,
          defaultValue: "{{name}} is already on D{{day}} in the selected weeks",
        }),
      );
      return;
    }
    // "empty" shouldn't be reachable (the bar hides on empty days), but
    // a silent tap is worse than a generic line if it ever is.
    setError(t("composer.cloneDayEmpty", { defaultValue: "Nothing to clone on this day" }));
  };

  return (
    <View style={{ gap: 8, marginTop: 4 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={() => {
          setOpen((o) => !o);
          setError(null);
        }}
        style={styles.addExercisesBtn}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Icon
            name="copy"
            size={styles.addExercisesText.fontSize}
            color={styles.addExercisesText.color}
          />
          <Text style={styles.addExercisesText}>
            {t("composer.cloneDay", { n: selectedDay + 1, defaultValue: "Clone D{{n}} to…" })}
          </Text>
        </View>
      </Pressable>
      {open ? (
        <View style={styles.chipsLine}>
          <Text style={styles.chipsLabel}>
            {t("composer.cloneDayTarget", { defaultValue: "To" })}
          </Text>
          <View style={styles.chipsRow}>
            {Array.from({ length: dayCount }, (_, d) => d)
              .filter((d) => d !== selectedDay)
              .map((d) => (
                <Pressable
                  key={d}
                  accessibilityRole="button"
                  onPress={() => clone(d)}
                  hitSlop={8}
                  style={styles.chip}
                >
                  <Text style={styles.chipText}>D{d + 1}</Text>
                </Pressable>
              ))}
          </View>
        </View>
      ) : null}
      {error ? <Text style={styles.validationText}>{error}</Text> : null}
    </View>
  );
}
